import { useState, useEffect } from 'react';
import api from '../../hooks/useApi';
import { formatDate } from '../../utils/format';
import { PAGE_LABELS } from '../../utils/rbac';
import PhotoAvatar from '../shared/Avatar';

const PERIOD_OPTIONS = [
  { label: '7 days',  value: 7 },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
];

function Avatar({ name, color, size = 24, personId }) {
  const initials = (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const bg = color || '#334155';
  return (
    <PhotoAvatar as="span" personId={personId}
      style={{ background: bg, width: size, height: size, fontSize: size * 0.38, lineHeight: `${size}px` }}
      className="inline-flex items-center justify-center rounded-full text-white font-bold shrink-0 select-none"
    >
      {initials}
    </PhotoAvatar>
  );
}

function Bar({ value, max, color = 'bg-navy' }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[9px] font-bold text-gray-600 w-8 text-right tabular-nums">{value}</span>
    </div>
  );
}

function SparkLine({ data }) {
  if (!data || data.length < 2) return null;
  const vals = data.map(d => d.active_users);
  const max  = Math.max(...vals, 1);
  const W = 120, H = 28;
  const pts = vals.map((v, i) => {
    const x = (i / (vals.length - 1)) * W;
    const y = H - (v / max) * (H - 2);
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="overflow-visible">
      <polyline points={pts} fill="none" stroke="#b5a14a" strokeWidth="1.5" strokeLinejoin="round" />
      {vals.map((v, i) => (
        <circle key={i} cx={(i / (vals.length - 1)) * W} cy={H - (v / max) * (H - 2)} r="2" fill="#b5a14a" />
      ))}
    </svg>
  );
}

export default function AppAdoptionPage() {
  const [days, setDays]     = useState(30);
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get('activity/adoption', { days })
      .then(r => { setData(r); setLoading(false); })
      .catch(() => setLoading(false));
  }, [days]);

  const maxWrites = data ? Math.max(...data.topWriters.map(u => u.write_count), 1) : 1;
  const maxViews  = data ? Math.max(...data.topViewers.map(u => u.view_count), 1) : 1;
  const maxPage   = data ? Math.max(...data.pagePopularity.map(p => p.views), 1) : 1;

  const totalActive = data
    ? new Set([
        ...data.topWriters.map(u => u.person_id),
        ...data.topViewers.map(u => u.person_id),
      ]).size
    : 0;

  const pageLabel = (p) => PAGE_LABELS[p] || p;

  return (
    <div className="fade-in">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-extrabold text-navy">App Adoption</h2>
          <p className="text-[9px] text-gray-500">Who's using the platform, what they're viewing, and who hasn't been seen recently.</p>
        </div>
        <div className="flex gap-1">
          {PERIOD_OPTIONS.map(o => (
            <button key={o.value} onClick={() => setDays(o.value)}
              className={`px-2.5 py-1 text-[9px] font-semibold rounded transition-colors ${days === o.value ? 'bg-navy text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[...Array(6)].map((_, i) => <div key={i} className="h-40 bg-gray-100 rounded-lg animate-pulse" />)}
        </div>
      ) : !data ? (
        <div className="text-center py-16 text-[10px] text-gray-400">Failed to load adoption data</div>
      ) : (
        <>
          {/* Summary row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
            {[
              { label: `Active last ${days}d`,   val: totalActive,         color: 'text-navy' },
              { label: 'Unique pages visited',   val: data.pagePopularity.length, color: 'text-gold' },
              { label: 'Inactive users',          val: data.inactive.length, color: data.inactive.length > 0 ? 'text-red-500' : 'text-emerald-600' },
              { label: 'DAU peak',               val: data.dailyActive.length > 0 ? Math.max(...data.dailyActive.map(d => d.active_users)) : 0, color: 'text-blue-600' },
            ].map(s => (
              <div key={s.label} className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="text-[8px] text-gray-500 uppercase font-bold">{s.label}</div>
                <div className={`text-2xl font-extrabold ${s.color}`}>{s.val}</div>
              </div>
            ))}
          </div>

          {/* Daily Active Users sparkline */}
          {data.dailyActive.length > 1 && (
            <div className="bg-white border border-gray-200 rounded-lg p-3 mb-4 flex items-center gap-4">
              <div>
                <div className="text-[8px] text-gray-500 uppercase font-bold mb-1">Daily Active Users — last {days} days</div>
                <SparkLine data={data.dailyActive} />
              </div>
              <div className="ml-auto text-[8px] text-gray-400 italic text-right">
                {data.dailyActive[0]?.day} → {data.dailyActive[data.dailyActive.length - 1]?.day}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

            {/* Top writers */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-3 py-2 border-b border-gray-100">
                <h3 className="text-[11px] font-bold text-gray-800">Top Contributors</h3>
                <p className="text-[8px] text-gray-400">Ranked by writes & updates (audit trail)</p>
              </div>
              {data.topWriters.length === 0 ? (
                <div className="p-6 text-center text-[10px] text-gray-400">No write activity yet</div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {data.topWriters.map((u, i) => (
                    <div key={u.person_id} className="flex items-center gap-2 px-3 py-2">
                      <span className="text-[9px] font-bold text-gray-300 w-4 text-right">{i + 1}</span>
                      <Avatar name={u.name} color={u.avatar_color} size={22} personId={u.person_id} />
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-semibold text-gray-800 truncate">{u.name || 'Unknown'}</div>
                        <Bar value={u.write_count} max={maxWrites} color="bg-navy" />
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-[9px] font-bold text-navy">{u.write_count}</div>
                        <div className="text-[7px] text-gray-400">{u.last_active ? formatDate(u.last_active.split('T')[0]) : '—'}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Top viewers */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-3 py-2 border-b border-gray-100">
                <h3 className="text-[11px] font-bold text-gray-800">Top Viewers</h3>
                <p className="text-[8px] text-gray-400">Ranked by page views (read activity)</p>
              </div>
              {data.topViewers.length === 0 ? (
                <div className="p-6 text-center text-[10px] text-gray-400">No view data yet — will populate as users navigate</div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {data.topViewers.map((u, i) => (
                    <div key={u.person_id} className="flex items-center gap-2 px-3 py-2">
                      <span className="text-[9px] font-bold text-gray-300 w-4 text-right">{i + 1}</span>
                      <Avatar name={u.name} color={u.avatar_color} size={22} personId={u.person_id} />
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-semibold text-gray-800 truncate">{u.name || 'Unknown'}</div>
                        <Bar value={u.view_count} max={maxViews} color="bg-gold" />
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-[9px] font-bold text-gold">{u.view_count}</div>
                        <div className="text-[7px] text-gray-400">{u.last_seen ? formatDate(u.last_seen.split('T')[0]) : '—'}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Page popularity */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-3 py-2 border-b border-gray-100">
                <h3 className="text-[11px] font-bold text-gray-800">Page Popularity</h3>
                <p className="text-[8px] text-gray-400">Most visited screens</p>
              </div>
              {data.pagePopularity.length === 0 ? (
                <div className="p-6 text-center text-[10px] text-gray-400">No view data yet</div>
              ) : (
                <div className="divide-y divide-gray-50 overflow-y-auto" style={{ maxHeight: 380 }}>
                  {data.pagePopularity.map(p => (
                    <div key={p.page} className="flex items-center gap-2 px-3 py-1.5">
                      <div className="flex-1 min-w-0">
                        <div className="text-[9px] font-semibold text-gray-700 truncate">{pageLabel(p.page)}</div>
                        <Bar value={p.views} max={maxPage} color="bg-blue-400" />
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-[9px] font-bold text-blue-600">{p.views}</div>
                        <div className="text-[7px] text-gray-400">{p.unique_users} user{p.unique_users !== 1 ? 's' : ''}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>

          {/* Inactive users */}
          {data.inactive.length > 0 && (
            <div className="bg-white border border-red-100 rounded-lg overflow-hidden mt-4">
              <div className="px-3 py-2 border-b border-red-100 flex items-center gap-2">
                <h3 className="text-[11px] font-bold text-red-700">Not Seen in {days} Days</h3>
                <span className="text-[8px] bg-red-100 text-red-600 font-bold px-1.5 py-0.5 rounded-full">{data.inactive.length}</span>
                <span className="text-[8px] text-gray-400 ml-auto italic">Active employees with a role who haven't appeared in any activity logs</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-0 divide-y sm:divide-y-0">
                {data.inactive.map(p => (
                  <div key={p.id} className="flex items-center gap-2 px-3 py-2 border-b border-gray-50">
                    <Avatar name={p.name} color={p.avatar_color} size={24} personId={p.id} />
                    <div className="min-w-0">
                      <div className="text-[10px] font-semibold text-gray-700 truncate">{p.name}</div>
                      <div className="text-[8px] text-gray-400 truncate">{p.user_role?.split(',').join(' · ')}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.inactive.length === 0 && (
            <div className="mt-4 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded text-[9px] text-emerald-700 font-semibold">
              All active users have been seen in the last {days} days.
            </div>
          )}
        </>
      )}
    </div>
  );
}
